import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import Header from "../components/header";
import Footer from "../components/footer";
import getApiBase from "../components/getApiBase";
import SubscriptionButton from "../components/SubscriptionButton";
import ShopMonthlyStatement from "../components/ShopMonthlyStatement";
import ApprovalPendingModal from "../components/ApprovalPendingModal";
import BillingRequiredModal from "../components/BillingRequiredModal";
import { useSubscription } from "../contexts/SubscriptionContext";
import { waitForSubscriptionUpdate } from "../utils/subscriptionPolling";
import { PREVIEW_BILLING_WEBHOOK_NOTICE, isVercelFeatureBranchDeploy, waitForBillingSetup } from "../utils/billingSetup";
import { hadBillingSetupReturnOnLoad, parseBillingSetupReturn } from "../utils/billingReturn";
import { openVoicePartnerOnboarding } from "../utils/voiceOnboarding";

const MechanicProfile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updateSubscriptions, subscriptions } = useSubscription();
  const checkoutHandledRef = useRef(false);
  const billingCheckoutHandledRef = useRef(false);
  const skipBillingStatusFromApiRef = useRef(hadBillingSetupReturnOnLoad());
  const billingReturn = parseBillingSetupReturn(searchParams);
  const billingSetupReturnPending = billingReturn.isSuccessReturn;
  const [billingConfirming, setBillingConfirming] = useState(billingReturn.isSuccessReturn);
  const [mechanicInfo, setMechanicInfo] = useState(null);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [ownerServiceHistory, setOwnerServiceHistory] = useState([]);
  const [selectedServiceDetail, setSelectedServiceDetail] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState({ id: null, name: "", status: null });
  
  // Edit Profile states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
  });
  const [requestChangeModalOpen, setRequestChangeModalOpen] = useState(false);
  const [changeRequestField, setChangeRequestField] = useState("");
  const [requestedValue, setRequestedValue] = useState("");
  const [changeReason, setChangeReason] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Email change states
  const [emailChangeModalOpen, setEmailChangeModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  const [hasPaidSubscription, setHasPaidSubscription] = useState(false);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [allShops, setAllShops] = useState([]);
  const [isLoadingShops, setIsLoadingShops] = useState(false);
  const [selectedShopId, setSelectedShopId] = useState(null);
  const [openBillingAfterCheckout, setOpenBillingAfterCheckout] = useState(false);
  const [billingReadyForQuotes, setBillingReadyForQuotes] = useState(false);
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [openingVoice, setOpeningVoice] = useState(false);

  const hasVoiceSubscription = (subscriptions || []).some(
    (s) =>
      s.plan?.name === "addon_ai_voice" &&
      (s.status === "active" || s.status === "trialing") &&
      s.cancel_at_period_end !== 1,
  );

  /** Shop-scoped Owner (supports co-owners). Falls back to Mechanics.role. */
  const shopRoleFor = (shopId) => {
    if (!shopId) return mechanicInfo?.role;
    const link = allShops.find((s) => s.id === shopId);
    if (link?.role) return link.role;
    return mechanicInfo?.role;
  };
  const isOwnerForShop = (shopId) => shopRoleFor(shopId) === "Owner";
  const canViewShopDemo = (shopId) => {
    const role = shopRoleFor(shopId);
    return role === "Owner" || role === "Sales";
  };

  // Active is enough for Owner dashboard/billing. in_network=false hides the
  // shop from the customer picker only — do not treat that as awaiting approval.
  const ownedActiveShops = allShops.filter(
    (s) =>
      s.status === "Active" &&
      (s.role === "Owner" || (!s.role && mechanicInfo?.role === "Owner")),
  );

  const effectiveShopId =
    selectedShopId || shop?.id || mechanicInfo?.shop_id || null;
  const billingReturnPath = effectiveShopId
    ? `/mechanic-profile?shopId=${effectiveShopId}`
    : "/mechanic-profile";

  useEffect(() => {
    const urlShopId = searchParams.get("shopId");
    if (urlShopId) {
      const parsed = parseInt(urlShopId, 10);
      if (!Number.isNaN(parsed)) {
        setSelectedShopId(parsed);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchEverything = async () => {
      try {
        const now = Date.now();
        let userData = JSON.parse(localStorage.getItem("user"));

        // ✅ Check if expired or missing
        if (!userData || !userData.expiry || now > userData.expiry) {
          localStorage.removeItem("user");

          const res = await fetch(`${getApiBase()}/api/auth/me`, {
            credentials: "include",
          });

          if (!res.ok) throw new Error("Unauthorized");

          const data = await res.json();

          // ✅ If your backend returns expiry timestamp:
          let expiryMs;
          if (data.expiry) {
            expiryMs = data.expiry * 1000;
          } else {
            // ✅ fallback to 2 hours
            expiryMs = now + 2 * 60 * 60 * 1000;
          }

          userData = { ...data.user, expiry: expiryMs };
          localStorage.setItem("user", JSON.stringify(userData));
        }

        setUser(userData);

        // ✅ Load mechanic profile
        const profileRes = await fetch(
          `${getApiBase()}/api/mechanicProfile/profile`,
          {
            credentials: "include",
          }
        );

        if (!profileRes.ok) throw new Error("Failed to fetch profile");

        const profileData = await profileRes.json();

        //console.log("Profile Data:", profileData);
        setMechanicInfo(profileData);
        setShop({ 
          id: profileData.shop_id, 
          name: profileData.shop_name,
          status: profileData.shop_status,
          claimStatus: profileData.claim_status  // ← NEW
        });

        let data = JSON.parse(localStorage.getItem("user"));
        data["is_active"] = profileData.is_active;
        localStorage.setItem("user", JSON.stringify(data));

        // Fetch all shops
        await fetchAllShops();

        // ✅ Role-based service history
        if (profileData.role === "Owner" || profileData.role === "Sales") {
          const [shopHistoryRes, subscriptionRes] = await Promise.all([
            fetch(`${getApiBase()}/api/mechanicProfile/shop-service-history`, {
              credentials: "include",
            }),
            fetch(`${getApiBase()}/api/mechanicProfile/subscription?mechanic_id=${profileData.id}&shop_id=${parseInt(searchParams.get("shopId") || profileData.shop_id, 10)}`, {
              credentials: "include",
            }),
          ]);

          if (shopHistoryRes.ok) {
            const shopHistoryData = await shopHistoryRes.json();
            const normalizedHistory = Array.isArray(shopHistoryData)
              ? shopHistoryData
              : Array.isArray(shopHistoryData?.history)
                ? shopHistoryData.history
                : [];
            setOwnerServiceHistory(normalizedHistory.filter(Boolean));
          }
          
          // Check subscription tier for invite feature
          if (subscriptionRes.ok) {
            const subscriptionData = await subscriptionRes.json();
            setHasPaidSubscription(subscriptionData.hasPaidSubscription || false);
            updateSubscriptions(subscriptionData.subscriptions || []);
            if (!skipBillingStatusFromApiRef.current) {
              setBillingReadyForQuotes(subscriptionData.billingReadyForQuotes || false);
            }
          }
        } else {
          const historyRes = await fetch(
            `${getApiBase()}/api/mechanicProfile/history`,
            { credentials: "include" }
          );
          if (historyRes.ok) setServiceHistory(await historyRes.json());
          
        }

        setLoading(false);
      } catch (err) {
        console.error("❌ Error:", err);
        localStorage.removeItem("user");
        navigate("/login");
      }
    };

    fetchEverything();
  }, []);

  // TASK-565: Stripe checkout return — sync session when webhooks miss preview
  useEffect(() => {
    if (!mechanicInfo?.id || !mechanicInfo?.shop_id) return;

    const status = searchParams.get("subscription");
    const sessionId = searchParams.get("session_id");
    if (status !== "success" || !sessionId || checkoutHandledRef.current) return;

    checkoutHandledRef.current = true;

    const confirmCheckout = async () => {
      setSnackbar({
        open: true,
        message: "Confirming your subscription...",
        severity: "info",
      });

      const result = await waitForSubscriptionUpdate({
        mechanicId: mechanicInfo.id,
        shopId: mechanicInfo.shop_id,
        sessionId,
        expectedPlanIds: [],
      });

      navigate("/mechanic-profile", { replace: true });

      if (result.success) {
        updateSubscriptions(result.subscriptions || []);
        setHasPaidSubscription(true);
        if (result.billingReadyForQuotes) {
          setBillingReadyForQuotes(true);
        }
        setOpenBillingAfterCheckout(true);
        const hasVoice = (result.subscriptions || []).some(
          (s) =>
            s.plan?.name === "addon_ai_voice" &&
            (s.status === "active" || s.status === "trialing"),
        );
        if (hasVoice && mechanicInfo.role === "Owner" && mechanicInfo.shop_id) {
          try {
            await openVoicePartnerOnboarding(
              selectedShopId || mechanicInfo.shop_id,
            );
            setSnackbar({
              open: true,
              message:
                "AI Phone Assistant activated — complete Voice setup in the new tab (accept invite).",
              severity: "success",
            });
          } catch (voiceErr) {
            console.error("Voice onboarding open failed:", voiceErr);
            setSnackbar({
              open: true,
              message:
                "Subscription activated. Open Manage subscriptions → Complete Voice setup if the invite tab did not open.",
              severity: "warning",
            });
          }
        } else {
          setSnackbar({
            open: true,
            message: "Subscription activated successfully!",
            severity: "success",
          });
        }
      } else {
        setSnackbar({
          open: true,
          message:
            "Payment received. If billing still shows Free Plan, open Billing & Subscriptions or refresh.",
          severity: "warning",
        });
      }
    };

    confirmCheckout();
  }, [mechanicInfo, searchParams, navigate, updateSubscriptions]);

  // TASK-567: Billing setup return — same inline pattern as subscription (TASK-565)
  useEffect(() => {
    if (!mechanicInfo?.id) return;

    const { isSuccessReturn, sessionId, shopId: urlShopId } = parseBillingSetupReturn(searchParams);
    if (!isSuccessReturn || !sessionId || billingCheckoutHandledRef.current) return;

    const billingShopId = urlShopId || mechanicInfo.shop_id;
    if (!billingShopId) return;

    billingCheckoutHandledRef.current = true;
    setBillingConfirming(true);
    skipBillingStatusFromApiRef.current = true;

    const confirmBillingSetup = async () => {
      setSnackbar({
        open: true,
        message: "Saving your payment method...",
        severity: "info",
      });

      const result = await waitForBillingSetup({
        mechanicId: mechanicInfo.id,
        shopId: billingShopId,
        sessionId,
      });

      if (result.success && result.billingReadyForQuotes) {
        skipBillingStatusFromApiRef.current = false;
        setBillingReadyForQuotes(true);
        setBillingModalOpen(false);
        sessionStorage.removeItem("billing_setup_dismissed");
        sessionStorage.removeItem("billing_setup_return_failed");
        const baseMessage =
          "Payment method saved. You can submit quotes on the Free plan.";
        setSnackbar({
          open: true,
          message: isVercelFeatureBranchDeploy()
            ? `${baseMessage} ${PREVIEW_BILLING_WEBHOOK_NOTICE}`
            : baseMessage,
          severity: "success",
        });
      } else {
        skipBillingStatusFromApiRef.current = false;
        sessionStorage.setItem("billing_setup_return_failed", "1");
        setSnackbar({
          open: true,
          message:
            result.error ||
            "Payment received. If quoting is still blocked, refresh or contact support.",
          severity: "error",
        });
      }

      setBillingConfirming(false);
      const cleanQuery = urlShopId ? `?shopId=${urlShopId}` : "";
      navigate(`/mechanic-profile${cleanQuery}`, { replace: true });
    };

    confirmBillingSetup();
  }, [mechanicInfo, searchParams, navigate]);

  // TASK-567: Prompt new/active owners without billing on file
  useEffect(() => {
    if (billingConfirming || billingSetupReturnPending) return;
    if (sessionStorage.getItem("billing_setup_return_failed")) return;

    if (
      isOwnerForShop(effectiveShopId) &&
      shop?.status === "Active" &&
      !billingReadyForQuotes &&
      !loading
    ) {
      const dismissed = sessionStorage.getItem("billing_setup_dismissed");
      if (!dismissed) {
        setBillingModalOpen(true);
      }
    }
  }, [mechanicInfo, shop, billingReadyForQuotes, loading, billingSetupReturnPending, billingConfirming]);

  useEffect(() => {
    //console.log("✅ Shop updated:", shop);
  }, [shop]);

  // Auto-open approval modal when shop is pending or shop info is null.
  // TASK-755: never block when Autoshops status is already Active (stale claim_status).
  useEffect(() => {
    const shopIsActive = shop?.status === "Active";
    if (shopIsActive) {
      setApprovalModalOpen(false);
      return;
    }
    if (
      shop?.claimStatus === "pending" ||
      shop?.status === "Pending" ||
      (mechanicInfo?.role === "Owner" && (!shop?.id || !shop?.name))
    ) {
      setApprovalModalOpen(true);
    }
  }, [shop, mechanicInfo]);

  useEffect(() => {
    //console.log("History updated:", serviceHistory);
  }, [serviceHistory]);

  // Update selectedShopId when allShops loads
  useEffect(() => {
    if (allShops.length > 0 && user?.id) {
      // Filter active shops
      // Active is enough to select a shop. in_network=false is marketplace hide only.
      const activeShops = allShops.filter((s) => s.status === "Active");
      
      if (activeShops.length > 0) {
        // Try to load from localStorage first
        const savedShopId = localStorage.getItem(`selectedShopId_${user.id}`);
        if (savedShopId) {
          const parsedId = parseInt(savedShopId);
          if (activeShops.some(s => s.id === parsedId)) {
            setSelectedShopId(parsedId);
            return;
          }
        }
        
        // Otherwise, select first active shop (or primary shop if available)
        // Only set if not already set to avoid unnecessary updates
        if (selectedShopId === null || !activeShops.some(s => s.id === selectedShopId)) {
          const primaryShop = activeShops.find(s => s.is_primary);
          setSelectedShopId(primaryShop?.id || activeShops[0].id);
        }
      }
    }
  }, [allShops, user]);

  // Keep Voice entitlement in sync when owner switches shop
  useEffect(() => {
    if (!selectedShopId || !mechanicInfo?.id || !isOwnerForShop(selectedShopId)) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${getApiBase()}/api/mechanicProfile/subscription?mechanic_id=${mechanicInfo.id}&shop_id=${selectedShopId}`,
          { credentials: "include" },
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) {
          updateSubscriptions(data.subscriptions || []);
          setHasPaidSubscription(data.hasPaidSubscription || false);
        }
      } catch (err) {
        console.error("Subscription refresh failed:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedShopId, mechanicInfo?.id, mechanicInfo?.role, allShops, updateSubscriptions]);

  // Fetch all shops
  const fetchAllShops = async () => {
    setIsLoadingShops(true);
    
    try {
      const response = await fetch(`${getApiBase()}/api/mechanicDashboard/shops`, {
        credentials: 'include',
      });

      const data = await response.json();
      
      if (data.success) {
        setAllShops(data.shops || []);
      } else {
        console.error('Error fetching shops:', data.error);
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
    } finally {
      setIsLoadingShops(false);
    }
  };

  // Handle email change request
  const handleEmailChangeRequest = async () => {
    if (!newEmail.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter a new email address",
        severity: "error",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setSnackbar({
        open: true,
        message: "Please enter a valid email address",
        severity: "error",
      });
      return;
    }

    if (newEmail.toLowerCase() === mechanicInfo.email.toLowerCase()) {
      setSnackbar({
        open: true,
        message: "New email must be different from current email",
        severity: "error",
      });
      return;
    }

    setEmailChangeLoading(true);

    try {
      const response = await fetch(`${getApiBase()}/api/auth/request-email-change`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ new_email: newEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: "Verification email sent! Please check your new email address (including spam/junk folder).",
          severity: "success",
        });
        setEmailChangeModalOpen(false);
        setNewEmail("");
      } else {
        setSnackbar({
          open: true,
          message: data.error || "Failed to request email change",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Email change request error:", error);
      setSnackbar({
        open: true,
        message: "Failed to request email change. Please try again.",
        severity: "error",
      });
    } finally {
      setEmailChangeLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading your profile...</Typography>
      </Box>
    );
  }
  return (
    <>
      <Header />

      <Box id="mechProfile" sx={{ minHeight: "100vh", bgcolor: "#f7f9fc" }}>
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            p: 4,
          }}
        >
          <Box
            mb={3}
            sx={{
              mb: 4,
              p: 2,
              borderRadius: 2,
              boxShadow: 3,
              backgroundColor: "#e63926",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 2,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: "#fff",
                alignSelf: "flex-start",
                m: 0,
              }}
            >
              Service Advisor Profile
            </Typography>
            <Box
              display="flex"
              gap={2}
              flexWrap="wrap"
              alignItems="center"
              justifyContent="flex-end"
              sx={{ width: "100%" }}
            >
              {shop?.status === 'Pending' && (
                <Button
                  variant="outlined"
                  onClick={() => setApprovalModalOpen(true)}
                  sx={{
                    color: "#fff",
                    borderRadius: 2,
                    boxShadow: 3,
                    backgroundColor: "#fbbf24",
                    transition: "0.3s",
                    "&:hover": {
                      transform: "scale(1.03)",
                      backgroundColor: "#f59e0b",
                    },
                  }}
                >
                  ⏳ Pending Approval
                </Button>
              )}
              {((ownedActiveShops.length > 0) || canViewShopDemo(effectiveShopId)) && user?.id && (() => {
                const billingShopId =
                  (selectedShopId && canViewShopDemo(selectedShopId)
                    ? selectedShopId
                    : null) ||
                  ownedActiveShops[0]?.id ||
                  (canViewShopDemo(effectiveShopId) ? effectiveShopId : null);
                if (!billingShopId) return null;
                const salesView = shopRoleFor(billingShopId) === "Sales";
                return (
                  <>
                    {isOwnerForShop(billingShopId) && (
                      <ShopMonthlyStatement
                        mechanicId={user.id}
                        shops={ownedActiveShops}
                        initialShopId={billingShopId}
                      />
                    )}
                    <SubscriptionButton
                      mechanicId={user.id}
                      shopId={billingShopId}
                      openBillingModal={openBillingAfterCheckout}
                      billingLocked={salesView}
                    />
                  </>
                );
              })()}
              
              {/* Shop Selector and Dashboard Button */}
              {(() => {
                // Filter active shops
                const activeShops = allShops.filter((s) => s.status === "Active");
                const isOwner = isOwnerForShop(
                  activeShops.length === 1
                    ? activeShops[0].id
                    : selectedShopId || activeShops[0]?.id,
                );

                const dashboardBtnSx = {
                  color: "#fff",
                  borderRadius: 2,
                  boxShadow: 3,
                  backgroundColor: "#4285f4",
                  transition: "0.3s",
                  "&:hover": {
                    transform: "scale(1.03)",
                    backgroundColor: "#1564c0",
                  },
                };

                const handleOpenVoice = async () => {
                  if (!hasVoiceSubscription || !isOwner) return;
                  const targetShopId =
                    activeShops.length === 1
                      ? activeShops[0].id
                      : selectedShopId || activeShops[0]?.id || effectiveShopId;
                  if (!targetShopId) return;
                  try {
                    setOpeningVoice(true);
                    await openVoicePartnerOnboarding(targetShopId);
                  } catch (err) {
                    setSnackbar({
                      open: true,
                      message: err.message || "Could not open Voice Assistant",
                      severity: "error",
                    });
                  } finally {
                    setOpeningVoice(false);
                  }
                };

                const voiceButton =
                  isOwner && hasVoiceSubscription ? (
                  <Tooltip
                    title={
                      hasVoiceSubscription
                        ? "Open Autoshops Voice (accept invite / dashboard)"
                        : "Subscribe to AI Phone Assistant on Pricing to unlock"
                    }
                  >
                    <span>
                      <Button
                        variant="outlined"
                        onClick={handleOpenVoice}
                        disabled={!hasVoiceSubscription || openingVoice}
                        sx={{
                          ...dashboardBtnSx,
                          height: "40px",
                          textTransform: "none",
                          backgroundColor: hasVoiceSubscription
                            ? dashboardBtnSx.backgroundColor
                            : "#94a3b8",
                          "&.Mui-disabled": {
                            color: "#fff",
                            backgroundColor: "#94a3b8",
                          },
                          "&:hover": hasVoiceSubscription
                            ? dashboardBtnSx["&:hover"]
                            : undefined,
                        }}
                      >
                        {openingVoice ? (
                          <CircularProgress size={18} sx={{ color: "#fff" }} />
                        ) : (
                          "Open Voice Assistant"
                        )}
                      </Button>
                    </span>
                  </Tooltip>
                ) : null;

                // Handle shop selection change
                const handleShopChange = (e) => {
                  const newShopId = parseInt(e.target.value);
                  setSelectedShopId(newShopId);
                  if (user?.id) {
                    localStorage.setItem(`selectedShopId_${user.id}`, newShopId.toString());
                  }
                };
                
                // Navigate to dashboard
                const goToDashboard = () => {
                  if (activeShops.length === 0) {
                    setApprovalModalOpen(true);
                    return;
                  }
                  
                  const targetShopId = activeShops.length === 1 
                    ? activeShops[0].id 
                    : (selectedShopId || activeShops[0].id);
                  
                  if (targetShopId) {
                    navigate(`/mechanic-dashboard?shopId=${targetShopId}`);
                  } else {
                    setApprovalModalOpen(true);
                  }
                };
                
                // Format shop address for display
                const formatShopAddress = (shop) => {
                  const parts = [];
                  if (shop.street_address || shop.address) parts.push(shop.street_address || shop.address);
                  if (shop.city) parts.push(shop.city);
                  if (shop.state) parts.push(shop.state);
                  if (shop.zip_code || shop.zip) parts.push(shop.zip_code || shop.zip);
                  return parts.length > 0 ? parts.join(', ') : 'Address not available';
                };
                
                if (activeShops.length === 0) {
                  // No active shops - show button that opens approval modal
                  return (
                    <>
                      <Button
                        variant="outlined"
                        onClick={() => setApprovalModalOpen(true)}
                        sx={dashboardBtnSx}
                      >
                        Go to Dashboard
                      </Button>
                      {voiceButton}
                    </>
                  );
                } else if (activeShops.length === 1) {
                  // Single active shop - show simple button
                  return (
                    <>
                      <Button
                        variant="outlined"
                        onClick={goToDashboard}
                        sx={dashboardBtnSx}
                      >
                        Go to Dashboard
                      </Button>
                      {voiceButton}
                    </>
                  );
                } else {
                  // Multiple active shops - show dropdown + button
                  const currentSelectedId = selectedShopId || activeShops[0].id;
                  
                  return (
                    <>
                      <Select
                        value={currentSelectedId}
                        onChange={handleShopChange}
                        sx={{
                          minWidth: 300,
                          height: '40px',
                          backgroundColor: '#fff',
                          borderRadius: 2,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4285f4',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#1564c0',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#4285f4',
                          },
                        }}
                      >
                        {activeShops.map(shop => (
                          <MenuItem key={shop.id} value={shop.id}>
                            {shop.name || shop.shop_name} - {formatShopAddress(shop)}
                          </MenuItem>
                        ))}
                      </Select>
                      <Button
                        variant="outlined"
                        onClick={goToDashboard}
                        sx={{
                          ...dashboardBtnSx,
                          height: '40px',
                        }}
                      >
                        Go to Dashboard
                      </Button>
                      {voiceButton}
                    </>
                  );
                }
              })()}
            </Box>
          </Box>

          <Stack spacing={3}>
            {mechanicInfo && (
              <Paper
                elevation={2}
                sx={{
                  mb: 4,
                  p: { xs: 2, sm: 3 },
                  borderRadius: 2,
                  borderShadow: 3,
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#fff",
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 3,
                    backgroundColor: "#e63926",
                    flexDirection: "column",
                  }}
                  gutterBottom
                >
                  {mechanicInfo?.role === "Mechanic" ? "Service Advisor Info" : "Shop Owner Info"}
                </Typography>
                <Box sx={{ mx: 2, }}>
                  <Typography sx={{ fontSize: "1.1rem" }}>
                    <strong>Name:</strong> {mechanicInfo.first_name}{" "}
                    {mechanicInfo.last_name}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                  <Typography sx={{ fontSize: "1.1rem" }}>
                    <strong>Email:</strong> {mechanicInfo.email}
                  </Typography>
                    <Tooltip title="Change Email">
                      <IconButton
                        size="small"
                        onClick={() => setEmailChangeModalOpen(true)}
                        sx={{
                          color: "#4285f4",
                          "&:hover": { backgroundColor: "rgba(66, 133, 244, 0.1)" },
                        }}
                      >
                        <EmailIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography sx={{ fontSize: "1.1rem", mt: 1 }}>
                    <strong>Phone:</strong> {mechanicInfo.phone_number}
                  </Typography>
                  <Typography sx={{ fontSize: "1.1rem" }}>
                    <strong>Role:</strong> {mechanicInfo.role}
                  </Typography>
                  <Typography sx={{ fontSize: "1.1rem" }}>
                    <strong>Shop:</strong> {mechanicInfo.shop_name}
                  </Typography>
                  <Typography sx={{ fontSize: "1.1rem" }}>
                    <strong>Shop Location:</strong>{" "}
                    {mechanicInfo.shop_address
                      ? `${mechanicInfo.shop_address.street}, ${mechanicInfo.shop_address.city}, ${mechanicInfo.shop_address.state} ${mechanicInfo.shop_address.zip}`
                      : "N/A"}
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* Shops Section */}
            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "#e63926" }}>
                My Shops ({allShops.length})
              </Typography>

              {isLoadingShops ? (
                <Typography variant="body2" color="text.secondary">
                  Loading shops...
                </Typography>
              ) : allShops.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No shops associated with your account
                </Typography>
              ) : (
                allShops.map(shop => (
                  <Box
                    key={shop.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      backgroundColor: shop.is_primary ? '#eff6ff' : 'white',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {shop.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {shop.address}, {shop.city}, {shop.state} {shop.zip_code}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Role: <strong>{shop.role}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Status: <strong>{shop.status}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            In Network: <strong>{shop.in_network ? 'Yes' : 'No'}</strong>
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Joined: {new Date(shop.joined_at).toLocaleDateString()}
                        </Typography>
                      </Box>

                      {shop.is_primary && (
                        <Chip
                          label="Primary Shop"
                          size="small"
                          color="primary"
                        />
                      )}
                    </Box>
                  </Box>
                ))
              )}
            </Paper>

            {mechanicInfo?.role === "Mechanic" && (
              <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" gutterBottom>
                  Your Completed Services
                </Typography>
                {serviceHistory.length > 0 ? (
                  <List sx={{ maxHeight: "50vh", overflowY: "auto" }}>
                    {serviceHistory.map((history, i) => (
                      <ListItem
                        key={i}
                        divider
                        sx={{
                          display: "flex",
                          flexDirection: {
                            xs: "column",
                            sm: "row",
                          },
                          alignItems: {
                            xs: "start",
                            sm: "center",
                          },
                        }}
                      >
                        <ListItemText
                          primary={`Request #${history.request_id} - ${history.user_first_name} ${history.user_last_name}`}
                          secondary={`Completed: ${new Date(
                            history.activity_date
                          ).toLocaleDateString()} | Vehicle: ${history.year} ${
                            history.make
                          } ${history.model}`}
                          primaryTypographyProps={{
                            sx: { fontSize: "1.1rem", fontWeight: "bold" },
                          }}
                          secondaryTypographyProps={{
                            sx: { fontSize: "1.1rem" },
                          }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mb: { xs: 1, sm: 0 } }}
                          onClick={() => {
                            setSelectedServiceDetail(history);
                            setModalOpen(true);
                          }}
                        >
                          Read More
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography color="textSecondary" sx={{ py: 0.5 }}>No Services Completed</Typography>
                )}
              </Paper>
            )}

            {mechanicInfo?.role === "Owner" && (
              <Paper
                elevation={2}
                sx={{
                  mb: 4,
                  p: { xs: 2, sm: 3 },
                  borderRadius: 2,
                  borderShadow: 3,
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#fff",
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 3,
                    backgroundColor: "#e63926",
                    flexDirection: "column",
                  }}
                  gutterBottom
                >
                  All Completed Services
                </Typography>
                {ownerServiceHistory.length > 0 ? (
                  <List sx={{ maxHeight: "50vh", overflowY: "auto" }}>
                    {ownerServiceHistory.map((history, i) => (
                      <ListItem
                        key={i}
                        divider
                        sx={{
                          display: "flex",
                          flexDirection: {
                            xs: "column",
                            sm: "row",
                          },
                          alignItems: {
                            xs: "start",
                            sm: "center",
                          },
                        }}
                      >
                        <ListItemText
                          primary={`Request #${history.request_id} - ${history.user_first_name} ${history.user_last_name}`}
                          secondary={`Completed: ${new Date(
                            history.activity_date
                          ).toLocaleDateString()} | Vehicle: ${history.year} ${
                            history.make
                          } ${history.model}`}
                          primaryTypographyProps={{
                            sx: { fontSize: "1.1rem", fontWeight: "bold" },
                          }}
                          secondaryTypographyProps={{
                            sx: { fontSize: "1.1rem" },
                          }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mb: { xs: 1, sm: 0 } }}
                          onClick={() => {
                            setSelectedServiceDetail(history);
                            setModalOpen(true);
                          }}
                        >
                          Read More
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography color="textSecondary" sx={{ py: 0.5 }}>
                    No Services Completed
                  </Typography>
                )}
              </Paper>
            )}

            {selectedServiceDetail && (
              <Fade in={modalOpen}>
                <Box
                  sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    zIndex: 1300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Paper
                    sx={{ p: 4, borderRadius: 2, maxWidth: 500, width: "100%" }}
                  >
                    <Box
                      sx={{
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        backgroundColor: "#e63926",
                      }}
                    >
                      <Typography
                        sx={{ color: "#fff", fontSize: "1.1rem" }}
                        variant="h6"
                        gutterBottom
                      >
                        <strong>Service Details</strong>
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "1.1rem" }}>
                      <strong>Customer: </strong>{" "}
                      {selectedServiceDetail.user_first_name}{" "}
                      {selectedServiceDetail.user_last_name}
                    </Typography>
                    <Typography sx={{ fontSize: "1.1rem" }}>
                      <strong>Vehicle: </strong> {selectedServiceDetail.year}{" "}
                      {selectedServiceDetail.make} {selectedServiceDetail.model}
                    </Typography>
                    <Typography sx={{ fontSize: "1.1rem" }}>
                      <strong>Final Price: </strong> $
                      {selectedServiceDetail.final_price}
                    </Typography>
                    <Typography sx={{ fontSize: "1.1rem" }}>
                      <strong>Date: </strong>{" "}
                      {new Date(
                        selectedServiceDetail.activity_date
                      ).toLocaleDateString()}
                    </Typography>
                    <Typography sx={{ fontSize: "1.1rem" }}>
                      <strong>Notes: </strong>
                      {selectedServiceDetail.notes}
                    </Typography>

                    {selectedServiceDetail?.services?.length > 0 && (
                      <>
                        <Typography
                          sx={{ fontSize: "1.1rem" }}
                          variant="subtitle1"
                          mt={2}
                        >
                          <strong>Services Performed:</strong>
                        </Typography>
                        <ul>
                          {selectedServiceDetail.services.map((svc, i) => (
                            <li sx={{ fontSize: "1.1rem" }} key={i}>
                              {svc.service_name} – Parts: $
                              {svc.final_price_parts}, Labor: $
                              {svc.final_price_labor}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    {selectedServiceDetail?.mechanics?.length > 0 && (
                      <>
                        <Typography
                          sx={{ fontSize: "1.1rem" }}
                          variant="subtitle1"
                          mt={2}
                        >
                          <strong>Mechanics Involved:</strong>
                        </Typography>
                        <ul>
                          {selectedServiceDetail.mechanics.map((m, i) => (
                            <li sx={{ fontSize: "1.1rem" }} key={i}>
                              {m.first_name} {m.last_name}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    {selectedServiceDetail?.scannedObdCodes?.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography sx={{ mb: 1, fontSize: "1.1rem" }}>
                          <strong>OBD2 Code:</strong> scanned on service date
                        </Typography>
                        <ul
                          style={{ paddingLeft: "1.5rem", marginTop: "0.5rem" }}
                        >
                          {selectedServiceDetail.scannedObdCodes.map(
                            (codeObj, index) => (
                              <li key={index}>
                                <Typography variant="body1">
                                  <strong>{codeObj.code}</strong>
                                  {codeObj.description
                                    ? ` – ${codeObj.description}`
                                    : ""}
                                </Typography>
                              </li>
                            )
                          )}
                        </ul>
                      </Box>
                    )}

                    <Stack direction="row" justifyContent="flex-end" mt={2}>
                      <Button onClick={() => setModalOpen(false)}>Close</Button>
                    </Stack>
                  </Paper>
                </Box>
              </Fade>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Email Change Modal */}
      <Dialog
        open={emailChangeModalOpen}
        onClose={() => {
          setEmailChangeModalOpen(false);
          setNewEmail("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#e63926" }}>
          <EmailIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          Change Email Address
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 1 }}>
            A verification link will be sent to your new email address. You must verify it before the change takes effect.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Current Email:</strong> {mechanicInfo?.email}
          </Typography>
          <TextField
            autoFocus
            label="New Email Address"
            type="email"
            fullWidth
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Enter your new email"
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            📧 Check your spam/junk folder if you don't see the verification email.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setEmailChangeModalOpen(false);
              setNewEmail("");
            }}
            sx={{ color: "#666" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEmailChangeRequest}
            variant="contained"
            disabled={emailChangeLoading}
            sx={{
              backgroundColor: "#e63926",
              "&:hover": { backgroundColor: "#d32f2f" },
            }}
          >
            {emailChangeLoading ? <CircularProgress size={20} /> : "Send Verification Email"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Pending Modal - Persistent when shop is pending or null.
          TASK-755: not persistent / not shown when Autoshops is already Active. */}
      <ApprovalPendingModal 
        open={approvalModalOpen} 
        onClose={() => setApprovalModalOpen(false)}
        persistent={
          shop?.status !== "Active" &&
          (shop?.claimStatus === "pending" ||
            shop?.status === "Pending" ||
            (mechanicInfo?.role === "Owner" && (!shop?.id || !shop?.name)))
        }
      />

      <BillingRequiredModal
        open={billingModalOpen}
        onClose={() => {
          sessionStorage.setItem("billing_setup_dismissed", "1");
          setBillingModalOpen(false);
        }}
        isOwner={mechanicInfo?.role === "Owner"}
        mechanicId={mechanicInfo?.id}
        shopId={effectiveShopId}
        returnPath={billingReturnPath}
        onSetupComplete={() => {
          setBillingReadyForQuotes(true);
          sessionStorage.removeItem("billing_setup_dismissed");
          setBillingModalOpen(false);
        }}
      />

      <Footer />
    </>
  );
};

export default MechanicProfile;
